import { Button } from '@1trading/react/components/ui/button';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { ChevronsUpDown, Plus, Trash2 } from 'lucide-react';
import { type FieldArray, type FieldArrayPath, type FieldValues, type Path, type PathValue, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TAX_RESIDENCE_DEFAULTS, TAX_RESIDENCES_MAX, type TaxResidence, type TaxResidencySubject } from '@/features/tax-residency/schema';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useTaxIdentificationNumberFormats } from '@/hooks/queries/use-tax-identification-number-formats';
import { getPreviousTaxIdForCountry } from '@/utils/tax-identification-number';

type TaxResidencyFieldsetProps<T extends FieldValues> = {
  name: FieldArrayPath<T>;
  subject?: TaxResidencySubject;
};

const TaxResidencyFieldsetTaxIdentificationNumber = <T extends FieldValues>({ name, index, subject }: TaxResidencyFieldsetProps<T> & { index: number }) => {
  const form = useFormContext<T>();
  const { countriesAsOptions } = useMainstreamCountries();
  const { data: formats } = useTaxIdentificationNumberFormats();
  const { t } = useTranslation();
  const country: string = form.watch(`${name}.${index}.country` as Path<T>);
  const countryName = countriesAsOptions.find((option) => option.value === country)?.label;
  const coverage = formats?.coverage[country];
  const example = formats?.examples[country];

  if (!country) return null;

  if (coverage?.status === 'no-tin-issued') {
    return <p className="col-span-2 text-sm text-muted-foreground">{t('taxResidency.taxIdentificationNumberNotIssued', { country: countryName })}</p>;
  }

  const isIssuedOnRegistration = coverage?.status === 'issued-on-registration';
  const hints = [
    coverage?.name
      ? t('taxResidency.taxIdentificationNumberHint', {
          context: subject,
          country: countryName,
          name: coverage.abbreviation ? `${coverage.name} (${coverage.abbreviation})` : coverage.name,
        })
      : '',
    isIssuedOnRegistration ? t('taxResidency.taxIdentificationNumberIssuedOnRegistration', { context: subject }) : '',
  ].filter(Boolean);

  return (
    <FormField
      control={form.control}
      name={`${name}.${index}.taxId` as Path<T>}
      render={({ field }) => (
        <FormItem className="col-span-2">
          <FormLabel>
            {t('taxResidency.taxIdentificationNumber')}
            {isIssuedOnRegistration ? '' : '*'}
          </FormLabel>
          <FormControl>
            <Input
              {...field}
              placeholder={
                example
                  ? t('nationalClientIdentifier.withExample', { text: t('taxResidency.placeholders.taxIdentificationNumber', { context: subject }), example })
                  : t('taxResidency.placeholders.taxIdentificationNumber', { context: subject })
              }
            />
          </FormControl>
          {hints.length ? <FormDescription>{hints.join(' ')}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
TaxResidencyFieldsetTaxIdentificationNumber.displayName = 'TaxResidencyFieldsetTaxIdentificationNumber';

const TaxResidencyFieldset = <T extends FieldValues>({
  name,
  previousTaxId,
  subject,
}: TaxResidencyFieldsetProps<T> & {
  /** A TIN given before tax residency was collected, offered for a chosen country it is valid in */
  previousTaxId?: string;
}) => {
  const form = useFormContext<T>();
  const { fields, append, remove } = useFieldArray({ control: form.control, name });
  const { countriesAsOptions } = useMainstreamCountries();
  const { data: formats } = useTaxIdentificationNumberFormats();
  const { t } = useTranslation();
  const taxResidences = form.watch(name as Path<T>) as TaxResidence[];

  const otherTaxResidences = (index: number) => taxResidences.filter((_, otherIndex) => otherIndex !== index);

  const countryOptionsFor = (index: number) => {
    const otherCountries = otherTaxResidences(index).map(({ country }) => country);

    return countriesAsOptions.filter((option) => !otherCountries.includes(option.value));
  };

  const previousTaxIdFor = (country: string, index: number) => {
    if (!formats) return '';

    const otherTaxIds = otherTaxResidences(index).map(({ taxId }) => taxId);

    return getPreviousTaxIdForCountry({ country, otherTaxIds, previousTaxId, validate: formats.validate });
  };

  return (
    <div className="space-y-6">
      {fields.map((taxResidence, index) => (
        <div key={taxResidence.id} className="grid grid-cols-2 gap-x-4 gap-y-6 border-b pb-6 last-of-type:border-b-0 last-of-type:pb-0">
          <FormField
            control={form.control}
            name={`${name}.${index}.country` as Path<T>}
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('taxResidency.country')}*</FormLabel>
                <PopoverControlled>
                  {(_, setOpen) => (
                    <>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                            <PseudoInputLabel>
                              {field.value
                                ? countriesAsOptions.find((option) => option.value === field.value)?.label
                                : t('taxResidency.placeholders.country', { context: subject })}
                            </PseudoInputLabel>
                            <PseudoInputIcon>
                              <ChevronsUpDown />
                            </PseudoInputIcon>
                          </PseudoInputButton>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="p-0" stretch>
                        <PseudoInputOptions
                          empty={t('statuses.noResults')}
                          options={countryOptionsFor(index)}
                          placeholder={t('fundamentals.searchCountries')}
                          value={field.value}
                          onValueChange={(value) => {
                            setOpen(false);
                            // The options report a click on the selected country too, which must not replace a typed TIN
                            if (value === field.value) return;

                            field.onChange(value);
                            form.setValue(`${name}.${index}.taxId` as Path<T>, previousTaxIdFor(value, index) as PathValue<T, Path<T>>);
                            form.clearErrors(`${name}.${index}.taxId` as Path<T>);
                          }}
                        />
                      </PopoverContent>
                    </>
                  )}
                </PopoverControlled>
                <FormMessage />
              </FormItem>
            )}
          />
          <TaxResidencyFieldsetTaxIdentificationNumber<T> index={index} name={name} subject={subject} />
          {fields.length > 1 ? (
            <Button className="col-span-2 justify-self-end gap-2 text-destructive hover:text-destructive" size="sm" type="button" variant="ghost" onClick={() => remove(index)}>
              <Trash2 className="size-4" />
              {t('taxResidency.remove')}
            </Button>
          ) : null}
        </div>
      ))}
      {fields.length < TAX_RESIDENCES_MAX ? (
        <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => append({ ...TAX_RESIDENCE_DEFAULTS } as FieldArray<T, FieldArrayPath<T>>)}>
          <Plus className="size-4" />
          {t('taxResidency.add')}
        </Button>
      ) : null}
    </div>
  );
};
TaxResidencyFieldset.displayName = 'TaxResidencyFieldset';

export { TaxResidencyFieldset };
